import React from 'react';
import { Wheat, ShieldCheck, Handshake, Flag, Asterisk } from 'lucide-react';
import { useLang } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { production } from '../mock';

/**
 * variant:
 *  - 'pastry' → shows the main savoir-faire block (3 pillars + ingredients)
 *  - 'pate'   → shows only the "La Production" block
 *  - 'both'   → shows everything (default)
 */
const SavoirFaire = ({ variant = 'both' }) => {
  const { t } = useLang();
  const theme = useTheme();

  const ingredients = [
    { name: t.savoir.pistachio, image: 'https://images.unsplash.com/photo-1551238875-13b9d38454db?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' },
    { name: t.savoir.cashew, image: 'https://images.unsplash.com/photo-1723466998040-78d7e2ef6d72?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' },
    { name: t.savoir.walnut, image: 'https://images.unsplash.com/photo-1635843108103-9af0ea224a19?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' },
    { name: t.savoir.almond, image: 'https://images.unsplash.com/photo-1600188999986-331bec5f9a8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' }
  ];

  const items = [
    { icon: Wheat, title: t.savoir.i1t, text: t.savoir.i1d },
    { icon: ShieldCheck, title: t.savoir.i2t, text: t.savoir.i2d },
    { icon: Handshake, title: t.savoir.i3t, text: t.savoir.i3d }
  ];

  const showPastry = variant === 'pastry' || variant === 'both';
  const showPate = variant === 'pate' || variant === 'both';

  return (
    <section id="savoirfaire" className="bg-[#f6ecd9] py-28">
      <div className="max-w-6xl mx-auto px-6">
        {showPastry && (
          <>
            <div className="grid md:grid-cols-5 gap-10 mb-16">
              <div className="md:col-span-3">
                <p className="text-[11px] tracking-[0.35em] uppercase text-[#d4a94c] mb-6">
                  {t.savoir.eyebrow}
                </p>
                <h2 className="font-serif text-5xl md:text-6xl text-[#0d1b36] leading-[1.08]">
                  {t.savoir.t1}<br />
                  <em className="italic font-light">{t.savoir.t2}</em>
                </h2>
              </div>
              <div className="md:col-span-2 md:pt-20">
                <p className="text-[#0d1b36]/70 text-base leading-relaxed">
                  {t.savoir.desc}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-16 items-start">
              <div>
                <div className="space-y-10">
                  {items.map((item, i) => (
                    <div key={i} className="flex gap-5 group">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full border border-[#d4a94c]/50 bg-[#0d1b36]/5 flex items-center justify-center group-hover:bg-[#d4a94c] transition-colors duration-300">
                        <item.icon className="w-5 h-5 text-[#d4a94c] group-hover:text-[#0d1b36] transition-colors" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl text-[#0d1b36] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-[#0d1b36]/70 text-sm leading-relaxed">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <button className="mt-10 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0d1b36]/30 text-[#0d1b36] text-[10px] tracking-[0.3em] uppercase hover:border-[#d4a94c] hover:text-[#d4a94c] transition-all duration-300">
                  <Flag className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {t.savoir.madeIn}
                </button>
              </div>

              <div>
                <div className="rounded-2xl overflow-hidden aspect-square">
                  <img
                    src="https://images.unsplash.com/photo-1537734796389-e1fc293cf856?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Artisanal craft"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#0d1b36]/60 mt-6 mb-4">
                  {t.savoir.ingredients}
                </p>
                <div className="grid grid-cols-4 gap-3">
                  {ingredients.map((ing, i) => (
                    <div key={i} className="group">
                      <div className="rounded-lg overflow-hidden aspect-square">
                        <img
                          src={ing.image}
                          alt={ing.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-[#0d1b36]/70 mt-2 leading-tight">
                        {ing.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {showPate && (
          <div className={showPastry ? 'mt-24 pt-16 border-t border-[#0d1b36]/10' : ''}>
            <div className="text-center mb-10">
              <p className="font-serif italic text-xl md:text-2xl mb-1" style={{ color: theme.primary }}>
                La
              </p>
              <h3
                className="text-3xl md:text-5xl tracking-[0.08em] font-light"
                style={{ color: theme.primary }}
              >
                PRODUCTION
              </h3>
              <div className="flex items-center justify-center gap-4 mt-6">
                <span className="h-px w-14" style={{ background: theme.primary30 }} />
                <Asterisk className="w-4 h-4" style={{ color: theme.primary60 }} strokeWidth={1.5} />
                <span className="h-px w-14" style={{ background: theme.primary30 }} />
              </div>
            </div>

            <p
              className="text-center max-w-3xl mx-auto text-base leading-relaxed mb-10"
              style={{ color: theme.primary, opacity: 0.85 }}
            >
              {production.intro}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 max-w-4xl mx-auto">
              {production.machines.map((m, i) => {
                const isFilled = i % 2 === 1;
                return (
                  <div key={i} className="flex flex-col items-center text-center group">
                    <div
                      className="relative w-32 h-32 md:w-36 md:h-36 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:-translate-y-2"
                      style={{
                        background: isFilled ? theme.primary : theme.primary10,
                        border: isFilled ? 'none' : `1px solid ${theme.primary30}`
                      }}
                    >
                      <span
                        className="absolute font-serif text-6xl md:text-7xl opacity-20 select-none"
                        style={{ color: isFilled ? '#f6ecd9' : theme.primary }}
                      >
                        {m.count}
                      </span>
                      <div className="relative text-center px-3">
                        <p
                          className="text-[10px] tracking-[0.25em] uppercase font-semibold leading-tight"
                          style={{ color: isFilled ? '#f6ecd9' : theme.primary }}
                        >
                          {m.label}
                        </p>
                        <p
                          className="text-[10px] tracking-[0.25em] uppercase mt-1 leading-tight"
                          style={{ color: isFilled ? '#f6ecd9' : theme.primary, opacity: 0.9 }}
                        >
                          {m.sublabel}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SavoirFaire;
